import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, Checkbox, Text, useToast, Box, Input,
  Badge, Switch, Flex, HStack,
} from '@chakra-ui/react';
import api from '../api';

export default function StatusModal({ projectId, currentGroupIds, allGroups, viewGroups, isOpen, onClose, onRefresh }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [primaryId, setPrimaryId] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      const ids = currentGroupIds || [];
      setSelected(ids);
      // Find primary from props or default to first
      const primaryGroup = (allGroups || viewGroups || []).find(g => ids.includes(g.id) && g.is_primary);
      setPrimaryId(primaryGroup?.id || ids[0] || null);
      setShowAll(false);
      setSearch('');
    }
  }, [isOpen, currentGroupIds]);

  const handleToggle = (id) => {
    setSelected(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (next.length === 0) return prev;
      return next;
    });
    // If deselecting the primary, reassign to first remaining
    setPrimaryId(prev => {
      if (prev === id) {
        // Find first selected that isn't being removed
        const remaining = selected.filter(x => x !== id);
        return remaining[0] || null;
      }
      return prev;
    });
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      toast({ title: 'At least one group is required', status: 'warning', duration: 2000 });
      return;
    }
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/groups`, { row_group_ids: selected, primary_row_group_id: primaryId || selected[0] });
      toast({ title: 'Row groups updated', status: 'success', duration: 1500 });
      onRefresh();
      onClose();
    } catch (err) {
      toast({ title: 'Failed to update', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const displayGroups = showAll ? (allGroups || viewGroups || []) : (viewGroups || allGroups || []);
  const filteredGroups = search.trim()
    ? displayGroups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
    : displayGroups;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader fontSize="md">Add to Row Group</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Select one or more row groups. The project will appear in each selected group. Tap ★ to set the primary group (used for financial de-duplication).
          </Text>

          <Flex justify="space-between" align="center" mb={2}>
            <HStack spacing={2}>
              <Switch size="sm" colorScheme="blue" isChecked={showAll} onChange={e => setShowAll(e.target.checked)} />
              <Text fontSize="11px" color={showAll ? 'blue.600' : 'gray.500'} fontWeight={showAll ? '600' : '400'}>
                {showAll ? 'Show all Row Groups' : 'Show Row Groups in this view'}
              </Text>
            </HStack>
            <Badge colorScheme="gray" fontSize="9px">{filteredGroups.length}</Badge>
          </Flex>

          <Input size="sm" placeholder="Search row groups..." value={search}
            onChange={e => setSearch(e.target.value)} mb={2} />

          <VStack spacing={1} align="stretch" maxH="350px" overflowY="auto">
            {filteredGroups.map(g => (
              <Box
                key={g.id}
                p={2}
                borderRadius="md"
                bg={selected.includes(g.id) ? 'blue.50' : 'white'}
                border="1px"
                borderColor={selected.includes(g.id) ? 'blue.200' : 'gray.100'}
                cursor="pointer"
                _hover={{ bg: selected.includes(g.id) ? 'blue.100' : 'gray.50' }}
                onClick={() => handleToggle(g.id)}
              >
                <Flex align="center" justify="space-between" w="100%">
                  <Checkbox
                    size="sm"
                    isChecked={selected.includes(g.id)}
                    onChange={() => handleToggle(g.id)}
                    colorScheme="blue"
                  >
                    <Text fontSize="12px" fontWeight="500">{g.name}</Text>
                  </Checkbox>
                  {selected.includes(g.id) && (
                    <Box as="span" fontSize="14px" cursor="pointer" title="Set as primary group"
                      color={primaryId === g.id ? 'orange.400' : 'gray.300'}
                      fontWeight={primaryId === g.id ? '800' : '400'}
                      _hover={{ color: 'orange.400' }}
                      onClick={e => { e.stopPropagation(); setPrimaryId(g.id); }}>
                      ★
                    </Box>
                  )}
                </Flex>
              </Box>
            ))}
            {filteredGroups.length === 0 && (
              <Text fontSize="11px" color="gray.400" textAlign="center" py={4}>
                {search ? 'No groups match search' : 'No row groups available'}
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
            onClick={handleSave} isLoading={saving}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
